import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "../../Utills/toast";
import { ButtonLoader, SectionLoader } from "../../component/common/Loader";
import { useActionDialog } from "../../component/common/ActionDialog/ActionDialog";
import EmptyScreen from "../../component/common/EmptyScreen";
import MobileFilterCard from "../../component/common/MobileFilterCard";
import TakeawayOrderCard from "./TakeawayOrderCard";
import TakeawayBillingModal from "./TakeawayBillingModal";
import { getTakeawayOrders, updateTakeawayStatus } from "./takeawayApi";
import "./takeaway.css";

const titles = { live: "Live Takeaway Orders", history: "Takeaway History", cancelled: "Cancelled Takeaway Orders" };

const TakeawayOrders = ({ view = "live" }) => {
  const { confirmAction } = useActionDialog();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [billingOrder, setBillingOrder] = useState(null);
  const [tokenSearch, setTokenSearch] = useState("");
  const realtimeRevision = useSelector((state) => state.notifications?.takeawayRevision || 0);

  const filteredOrders = useMemo(() => {
    const query = tokenSearch.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => String(order.tokenNumber || "").toLowerCase().includes(query));
  }, [orders, tokenSearch]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { setOrders(await getTakeawayOrders(view)); }
    catch (error) { if (!silent) toast.error(error.message); }
    finally { if (!silent) setLoading(false); }
  }, [view]);

  useEffect(() => {
    load();
    if (view !== "live") return undefined;
    const timer = setInterval(() => load(true), 15000);
    return () => clearInterval(timer);
  }, [load, view, realtimeRevision]);

  const changeStatus = async (order, status) => {
    if (status >= 5) {
      const action = status === 5 ? "cancel" : "reject";
      const confirmed = await confirmAction({
        title: `${status === 5 ? "Cancel" : "Reject"} takeaway order`,
        message: `Are you sure you want to ${action} ${order.tokenNumber}? The order will move to cancellation history.`,
        confirmLabel: status === 5 ? "Cancel order" : "Reject order",
        danger: true,
      });
      if (!confirmed) return;
    }
    setBusy(order.takeawayOrderId);
    try {
      await updateTakeawayStatus(order.takeawayOrderId, status);
      toast.success(`Takeaway order ${order.tokenNumber} updated.`);
      await load(true);
    } catch (error) { toast.error(error.message); }
    finally { setBusy(""); }
  };

  return <div className="takeaway-page">
    <div className="takeaway-title"><div><h1>{titles[view]}</h1><p>{view === "live" ? "Auto-refreshes every 15 seconds" : "Latest 250 orders"}</p></div><div className="takeaway-actions"><button onClick={() => load()} disabled={loading} aria-busy={loading}>{loading ? <ButtonLoader label="Refreshing..." /> : "Refresh"}</button>{view === "live" && <Link to="/takeaway/new">New order</Link>}</div></div>
    <MobileFilterCard
      className="takeaway-order-toolbar"
      label="Search orders"
      hint="Enter a full or partial token number"
      placeholder="Search by token"
      searchAriaLabel="Search orders by token"
      search={tokenSearch}
      setSearch={setTokenSearch}
      countText={!loading ? `${tokenSearch.trim() ? filteredOrders.length : orders.length} ${(tokenSearch.trim() ? filteredOrders.length : orders.length) === 1 ? "order" : "orders"}${tokenSearch.trim() ? " found" : ""}` : null}
    />
    {loading ? <SectionLoader label="Loading orders..." rows={4} /> : orders.length === 0 ? <EmptyScreen message={`No ${view} takeaway orders yet.`} subText="New orders will appear here automatically." /> : filteredOrders.length === 0 ? <EmptyScreen message="No matching token" subText="Try another full or partial token number." actionLabel="Clear search" onAction={() => setTokenSearch("")} /> : <div className="takeaway-order-grid">{filteredOrders.map((order) => <TakeawayOrderCard key={order.takeawayOrderId} order={order} onStatus={changeStatus} onBilling={setBillingOrder} busy={busy === order.takeawayOrderId} readonly={view !== "live"} />)}</div>}
    {billingOrder && <TakeawayBillingModal order={billingOrder} onClose={() => setBillingOrder(null)} onUpdated={() => load(true)} />}
  </div>;
};

export default TakeawayOrders;
